import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getMainPageSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import {
  fetchAnalyticsData,
  fetchDrillingActivitiesV2Data,
  fetchEquipmentData,
  fetchSignalsData,
  fetchOverviewData,
  fetchOverviewKeyFiguresData,
  fetchPowerMgmtData,
  fetchEquipmentConfigurations,
  fetchRigEquipments
} from '@api/Rig';
import { API_ROLLUP, SUB_SECTIONS } from '@constants/index';
import { useTimezone } from '@hooks/useTimezone';
import { useCallback } from 'react';
import { logError } from '@api/Logging';

export const useMainPageQueries = () => {
  const queryClient = useQueryClient(); // Access queryClient instance
  const {
    selectedRig,
    startDate: start,
    endDate: end,
    selectedSection
  } = useSelector(getMainPageSlice);
  const { calculateDates } = useTimezone();
  const { dateStart: startDate, dateEnd: endDate } = calculateDates({startDate: start, endDate: end});
  const { OVERVIEW, ANALYTICS, DRILLING_ACTIVITIES, EQUIPMENT, POWER_MGMT } = SUB_SECTIONS;

  const rigOverviewQuery = useQuery({
    queryKey: ['overview', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled:
      !!selectedRig && !!startDate && !!endDate && [OVERVIEW, ANALYTICS].includes(selectedSection),
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchOverviewData(signal, queryKey[1], API_ROLLUP.DAY)
        .catch((err) => {
          logError(err, 'Error fetching overview data');
          return null;
        });
    }
  });

  const rigKeyFiguresQuery = useQuery({
    queryKey: ['keyFigures', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled: !!selectedRig && !!startDate && !!endDate && selectedSection === OVERVIEW,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchOverviewKeyFiguresData(signal, queryKey[1], API_ROLLUP.HOUR)
        .catch((err) => {
          logError(err, 'Error fetching overview key figures data');
          return null;
        });
    }
  });

  const rigAnalyticsQuery = useQuery({
    queryKey: ['analytics', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled:
      !!selectedRig && !!startDate && !!endDate && [OVERVIEW, ANALYTICS].includes(selectedSection),
    placeholderData: [],
    queryFn: ({ queryKey, signal }) => {
      return fetchAnalyticsData(signal, queryKey[1], API_ROLLUP.MIN)
        .then((resp) => {
          const rigData = resp?.data?.find(item => item.rigId === selectedRig.rigId);
          return rigData?.analytics ?? [];
        })
        .catch((err) => {
          logError(err, 'Error fetching analytics data');
          return [];
        });
    }
  });

  const rigDrillingActivityQuery = useQuery({
    queryKey: [
      'drillingActivity',
      { selectedRig: selectedRig, startDate: startDate, endDate: endDate }
    ],
    enabled: !!selectedRig && !!startDate && !!endDate && selectedSection === DRILLING_ACTIVITIES,
    placeholderData: {},
    queryFn: ({ queryKey, signal }) => {
      return fetchDrillingActivitiesV2Data(signal, queryKey[1], API_ROLLUP.HOUR)
        .catch((err) => {
          logError(err, 'Error fetching drilling activities data');
          return {};
        });
    }
  });

  const rigEquipmentConfigQuery = useQuery({
    queryKey: ['equipmentConfig', { selectedRig: selectedRig }],
    enabled: !!selectedRig,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchEquipmentConfigurations(signal, queryKey[1].selectedRig.rigId)
        .catch((err) => {
          logError(err, 'Error fetching equipment configurations');
          return null;
        });
    }
  });

  const getRigEquipmentsQuery = useQuery({
    queryKey: ['equipments', { selectedRig: selectedRig }],
    enabled: !!selectedRig,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchRigEquipments(signal, queryKey[1].selectedRig.rigId)
        .catch((err) => {
          logError(err, 'Error fetching rig equipments');
          return null;
        });
    }
  })

  const rigEquipmentQuery = useQuery({
    queryKey: ['equipment', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled: !!rigEquipmentConfigQuery.data && !!selectedRig && !!startDate && !!endDate && [EQUIPMENT, OVERVIEW].includes(selectedSection),
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchEquipmentData(signal, queryKey[1], API_ROLLUP.DAY)
        .catch((err) => {
          logError(err, 'Error fetching equipment data');
          return null;
        });
    }
  });

  const rigEquipmentSignalsQuery = useQuery({
    queryKey: ['equipmentSignal', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled: !!rigEquipmentQuery.data && !!selectedRig && !!startDate && !!endDate && [EQUIPMENT].includes(selectedSection),
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchSignalsData(signal, queryKey[1], API_ROLLUP.DAY);
    }
  });


  const rigPowerMgmtQuery = useQuery({
    queryKey: ['powerMgmt', { selectedRig: selectedRig, startDate: startDate, endDate: endDate }],
    enabled: !!selectedRig && !!startDate && !!endDate && selectedSection === POWER_MGMT,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return fetchPowerMgmtData(signal, queryKey[1], API_ROLLUP.DAY);
    }
  });

  const resetMainPageData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['overview'] });
    await queryClient.invalidateQueries({ queryKey: ['analytics'] });
    await queryClient.invalidateQueries({ queryKey: ['powerMgmt'] });
    await queryClient.invalidateQueries({ queryKey: ['equipment'] });
    await queryClient.invalidateQueries({ queryKey: ['equipmentSignal'] });
    await queryClient.invalidateQueries({ queryKey: ['drillingActivities'] });
    await queryClient.invalidateQueries({ queryKey: ['keyFigures'] });
  }, [queryClient]);

  const resetAnalyticsData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['analytics'] });
  }, [queryClient]);

  return {
    rigOverviewQuery,
    rigPowerMgmtQuery,
    rigKeyFiguresQuery,
    rigEquipmentQuery,
    rigEquipmentSignalsQuery,
    rigEquipmentConfigQuery,
    rigDrillingActivityQuery,
    rigAnalyticsQuery,
    resetMainPageData,
    resetAnalyticsData,
    getRigEquipmentsQuery
  };
};
